import { useState, useEffect, useCallback, useRef } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomButton from "../../Components/CustomButton";
import CustomTable from "../../Components/CustomTable";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faRotateLeft,
  faTriangleExclamation,
  faCheckCircle,
  faBan,
  faClock,
  faMobileScreen,
  faCreditCard,
  faCircleInfo,
  faHourglassHalf,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";

import "./style.css";

const TABS = [
  { key: "eligible", label: "Issue a Refund" },
  { key: "all", label: "All Purchases" },
  { key: "apple-purchases", label: "Apple Purchases" },
  { key: "history", label: "Refund History" },
  { key: "apple", label: "Apple Refund Requests" },
];

const money = (amount, currency = "USD") =>
  `${currency === "USD" ? "$" : currency + " "}${Number(amount || 0).toFixed(2)}`;

/**
 * Refund management.
 *
 * Written for a non-technical operator: the wording avoids payment jargon, and
 * every screen states the consequence of an action before it is taken. The
 * numbers shown come straight from the backend, which derives them from the
 * credit ledger — the panel never calculates money itself, so what the admin
 * sees is always what will actually be refunded.
 */
const RefundManagement = () => {
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("login");

  const authHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const [activeTab, setActiveTab] = useState("eligible");
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [history, setHistory] = useState([]);
  const [appleRequests, setAppleRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchasePages, setPurchasePages] = useState(1);
  const eligibleRequest = useRef(0);
  const [declining, setDeclining] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const purchaseTab = ["eligible", "all", "apple-purchases"].includes(activeTab);


  // Refund modal
  const [selected, setSelected] = useState(null);
  const [refundMode, setRefundMode] = useState("full");
  const [partialCredits, setPartialCredits] = useState("");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const refundSource = selected?.provider === "apple" ? "apple" : activeTab === "eligible" ? "reviewed" : "all";

  /* ----------------------------------------------------------------- *
   * Loading
   * ----------------------------------------------------------------- */

  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/refunds/summary`, { headers: authHeaders });
      const json = await res.json();
      if (json?.status) setSummary(json.data);
    } catch (e) {
      // The dashboard tiles are informational; a failure here must not block
      // the operator from issuing a refund.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const loadEligible = useCallback(async () => {
    const requestId = ++eligibleRequest.current;
    setLoading(true);
    setEligible([]);
    try {
      const scope = activeTab === "eligible" ? "reviewed" : activeTab === "apple-purchases" ? "apple" : "all";
      const query = new URLSearchParams({ scope, page: String(purchasePage), search });
      const res = await fetch(`${apiUrl}/admin/refunds/eligible?${query}`, { headers: authHeaders });
      const json = await res.json();
      if (requestId !== eligibleRequest.current) return;
      setEligible(json?.status ? json.data?.data || [] : []);
      setPurchasePages(json?.data?.last_page || 1);
      if (json?.status && purchasePage > (json.data?.last_page || 1)) setPurchasePage(json.data?.last_page || 1);
      if (!json?.status) toast.error(json?.message || "Could not load purchases.");
    } catch (e) {
      if (requestId === eligibleRequest.current) toast.error("Could not load purchases.");
    }
    if (requestId === eligibleRequest.current) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, search, activeTab, purchasePage]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await fetch(`${apiUrl}/admin/refunds${query}`, { headers: authHeaders });
      const json = await res.json();
      setHistory(json?.status ? json.data?.data || [] : []);
    } catch (e) {
      toast.error("Could not load refund history.");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, search]);

  const loadAppleRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/admin/consumption-requests`, { headers: authHeaders });
      const json = await res.json();
      setAppleRequests(json?.status ? json.data?.data || [] : []);
    } catch (e) {
      toast.error("Could not load Apple refund requests.");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (purchaseTab) loadEligible();
    if (activeTab === "history") loadHistory();
    if (activeTab === "apple") loadAppleRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search, purchasePage]);

  /* ----------------------------------------------------------------- *
   * Refund flow
   * ----------------------------------------------------------------- */

  const openRefund = async (row) => {
    // Re-fetch rather than trusting the list row, so the figures shown in the
    // modal are current even if the list has been open for a while.
    try {
      const res = await fetch(`${apiUrl}/admin/refunds/quote/${row.user_subscription_id}`, {
        headers: authHeaders,
      });
      const json = await res.json();
      if (!json?.status) {
        toast.error(json?.message || "Could not load this purchase.");
        return;
      }
      setSelected(json.data);
      setRefundMode("full");
      setPartialCredits("");
      setReason("");
      setConfirming(false);
    } catch (e) {
      toast.error("Could not load this purchase.");
    }
  };

  const creditsToRefund =
    refundMode === "full"
      ? selected?.max_refundable_credits || 0
      : parseInt(partialCredits || "0", 10) || 0;

  const amountToRefund = selected ? (selected.per_credit || 0) * creditsToRefund : 0;

  const partialError = (() => {
    if (refundMode !== "partial") return null;
    if (partialCredits === "") return null;
    if (creditsToRefund < 1) return "Enter at least 1 credit.";
    if (creditsToRefund > (selected?.max_refundable_credits || 0)) {
      return `Only ${selected?.max_refundable_credits} unused credit(s) can be refunded.`;
    }
    return null;
  })();

  const canSubmit = selected && selected.can_refund && creditsToRefund > 0 && !partialError && !submitting
    && (refundSource !== "all" || reason.trim().length > 0);

  const declineRefund = async () => {
    if (!declining || !declineReason.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${apiUrl}/admin/subscription-cancellation-requests/${declining.cancellation_request.id}/decline-refund`, {
        method: "POST", headers: authHeaders, body: JSON.stringify({ reason: declineReason.trim() }),
      });
      const json = await res.json();
      if (!json?.status) throw new Error(json?.message || "Could not decline refund.");
      toast.success(json.message);
      setDeclining(null);
      loadEligible();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitRefund = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${apiUrl}/admin/refunds`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          user_subscription_id: selected.user_subscription_id,
          kind: refundMode,
          credits: refundMode === "partial" ? creditsToRefund : null,
          reason: reason.trim() || null,
          source: refundSource,
          cancellation_request_id: refundSource === "reviewed" ? selected.cancellation_request?.id : null,
        }),
      });
      const json = await res.json();

      if (json?.status) {
        toast.success(json.message);
        setSelected(null);
        loadSummary();
        loadEligible();
      } else {
        // The backend's refusal messages are written for the operator.
        toast.error(json?.message || "The refund could not be completed.");
        setConfirming(false);
      }
    } catch (e) {
      toast.error("The refund could not be completed.");
      setConfirming(false);
    }
    setSubmitting(false);
  };

  /* ----------------------------------------------------------------- *
   * Apple requests
   * ----------------------------------------------------------------- */

  const submitToApple = async (request, preference) => {
    try {
      const res = await fetch(`${apiUrl}/admin/consumption-requests/${request.id}/submit`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ refund_preference: preference }),
      });
      const json = await res.json();
      json?.status ? toast.success(json.message) : toast.error(json?.message);
      loadAppleRequests();
      loadSummary();
    } catch (e) {
      toast.error("Could not send this to Apple.");
    }
  };

  /* ----------------------------------------------------------------- *
   * Render
   * ----------------------------------------------------------------- */

  const providerBadge = (provider) =>
    provider === "apple" ? (
      <span className="refundProvider apple">
        <FontAwesomeIcon icon={faMobileScreen} /> Apple
      </span>
    ) : (
      <span className="refundProvider stripe">
        <FontAwesomeIcon icon={faCreditCard} /> Card
      </span>
    );

  const statusBadge = (status, label) => {
    const map = {
      succeeded: { cls: "ok", icon: faCheckCircle },
      pending: { cls: "warn", icon: faHourglassHalf },
      failed: { cls: "bad", icon: faTriangleExclamation },
      declined_by_provider: { cls: "bad", icon: faBan },
      reversed: { cls: "muted", icon: faRotateLeft },
    };
    const style = map[status] || { cls: "muted", icon: faCircleInfo };

    return (
      <span className={`refundStatus ${style.cls}`}>
        <FontAwesomeIcon icon={style.icon} /> {label || status}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="refundScreen">
        <div className="refundHeader">
          <h1>
            <FontAwesomeIcon icon={faRotateLeft} /> Refunds
          </h1>
          <p className="refundIntro">
            Refund unused credits for card purchases. Apple decides refunds for purchases
            billed through Apple. A local request keeps access and credits unchanged until
            Apple confirms its decision.
          </p>
        </div>

        {summary && (
          <div className="refundTiles">
            <div className="refundTile">
              <span className="tileLabel">Refunded so far</span>
              {summary.totals_by_currency?.length ? summary.totals_by_currency.map((total) => (
                <strong key={total.currency}>{money(total.amount, total.currency)}</strong>
              )) : <strong>{money(summary.total_refunded, summary.currency || "USD")}</strong>}
              <small>{summary.refund_count} refund(s)</small>
            </div>
            <div className="refundTile">
              <span className="tileLabel">Full / Partial</span>
              <strong>
                {summary.full_refunds} / {summary.partial_refunds}
              </strong>
              <small>by type</small>
            </div>
            <div className="refundTile">
              <span className="tileLabel">Credits withdrawn</span>
              <strong>{summary.credits_reversed}</strong>
              <small>{summary.credits_consumed_on_refunds} were already used</small>
            </div>
            <div className={`refundTile ${summary.open_apple_requests > 0 ? "alert" : ""}`}>
              <span className="tileLabel">Apple requests waiting</span>
              <strong>{summary.open_apple_requests}</strong>
              <small>needs an answer within 12 hours</small>
            </div>
          </div>
        )}

        <div className="refundTabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={activeTab === tab.key ? "active" : ""}
              onClick={() => {
                // Re-clicking the tab you're already on must not clear the list:
                // none of the effect's deps (activeTab/search/purchasePage) would
                // change, so no reload would run and it would show an empty
                // "No matching purchases." Only reset when actually switching.
                if (activeTab === tab.key) return;
                eligibleRequest.current++;
                setEligible([]);
                setPurchasePage(1);
                setActiveTab(tab.key);
              }}
            >
              {tab.label}
              {tab.key === "apple" && summary?.open_apple_requests > 0 && (
                <span className="tabBadge">{summary.open_apple_requests}</span>
              )}
            </button>
          ))}
        </div>

        {activeTab !== "apple" && (
          <div className="refundSearch">
            <input
              type="text"
              placeholder="Search by customer name or email"
              value={search}
              onChange={(e) => { eligibleRequest.current++; setSearch(e.target.value); setPurchasePage(1); }}
            />
          </div>
        )}

        {/* ---------------- Issue a refund ---------------- */}
        {purchaseTab && (
          <>
          <p className="refundNotice">
            {activeTab === "eligible"
              ? "Reviewed cancellations awaiting your refund decision. Completed, processing, declined and zero-balance cases are excluded."
              : activeTab === "all"
                ? "Search all purchases for exceptional refunds. A reason is required for card refunds. This action can proceed without cancellation review."
                : "Apple manages these purchases. Local requests do not issue money or cancel access."}
          </p>
          <CustomTable
            headers={[
              { key: "customer", title: "Customer" },
              { key: "plan", title: "What they bought" },
              { key: "paid", title: "Paid", align: "right" },
              { key: "used", title: "Credits used", align: "center" },
              { key: "left", title: "Credits left", align: "center" },
              { key: "refundable", title: "Can refund", align: "right" },
              { key: "action", title: "", align: "right" },
            ]}
          >
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="refundEmpty">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && eligible.length === 0 && (
                <tr>
                  <td colSpan={7} className="refundEmpty">
                    {activeTab === "eligible" ? "No reviewed cancellations awaiting a refund decision." : "No matching purchases."}
                  </td>
                </tr>
              )}
              {!loading &&
                eligible.map((row) => (
                  <tr key={row.user_subscription_id}>
                    <td>
                      <div className="refundCustomer">
                        <strong>{row.user?.name}</strong>
                        <small>{row.user?.email}</small>
                      </div>
                    </td>
                    <td>
                      {row.plan}
                      <br />
                      {providerBadge(row.provider)}
                      <small className="d-block">{row.status} · {String(row.refund_state || "").replaceAll("_", " ")}</small>
                      {row.cancellation_request?.refund_declined_reason && <small className="refundDeclineReason">Declined: {row.cancellation_request.refund_declined_reason}</small>}
                    </td>
                    <td className="text-end">{money(row.price_paid, row.currency)}</td>
                    <td className="text-center">
                      {row.credits_consumed} of {row.credits_granted}
                    </td>
                    <td className="text-center">
                      <strong>{row.credits_remaining}</strong>
                    </td>
                    <td className="text-end">
                      {row.provider === "apple" ? <span>Apple decides</span> : row.can_refund ? (
                        <strong>{money(row.max_refundable_amount, row.currency)}</strong>
                      ) : (
                        <span className="refundNothing">Nothing left</span>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="refundRowActions">
                      <CustomButton
                        type="button"
                        variant="primaryButton"
                        text={row.provider === "apple" ? "Apple refund details" : "Refund"}
                        disabled={row.provider !== "apple" && !row.can_refund}
                        onClick={() => openRefund(row)}
                      />
                      {activeTab === "eligible" && row.cancellation_request && (
                        <CustomButton type="button" variant="secondaryButton" className="refundDeclineButton"
                          text="Decline refund" icon={<FontAwesomeIcon icon={faBan} aria-hidden="true" />}
                          onClick={() => { setDeclining(row); setDeclineReason(""); }} />
                      )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </CustomTable>
          <div className="refundPagination">
            <CustomButton type="button" variant="secondaryButton" text="Previous" disabled={loading || purchasePage <= 1} onClick={() => setPurchasePage(purchasePage - 1)} />
            <span aria-live="polite">Page {purchasePage} of {purchasePages}</span>
            <CustomButton type="button" variant="secondaryButton" text="Next" disabled={loading || purchasePage >= purchasePages} onClick={() => setPurchasePage(purchasePage + 1)} />
          </div>
          </>
        )}

        {/* ---------------- History ---------------- */}
        {activeTab === "history" && (
          <CustomTable
            headers={[
              { key: "date", title: "Date" },
              { key: "customer", title: "Customer" },
              { key: "type", title: "Type" },
              { key: "credits", title: "Credits withdrawn", align: "center" },
              { key: "amount", title: "Amount", align: "right" },
              { key: "status", title: "Status" },
              { key: "by", title: "Refunded by" },
              { key: "reason", title: "Reason" },
            ]}
          >
            <tbody>
              {!loading && history.length === 0 && (
                <tr>
                  <td colSpan={8} className="refundEmpty">
                    No refunds yet.
                  </td>
                </tr>
              )}
              {history.map((row) => (
                <tr key={row.id}>
                  <td>{(row.created_at || "").slice(0, 10)}</td>
                  <td>
                    <div className="refundCustomer">
                      <strong>{row.user?.name}</strong>
                      <small>{row.user?.email}</small>
                    </div>
                  </td>
                  <td>
                    <span className="refundKind">{row.kind === "full" ? "Full" : "Partial"}</span>
                    <br />
                    {providerBadge(row.provider)}
                  </td>
                  <td className="text-center">{row.credits_refunded}</td>
                  <td className="text-end">{money(row.amount, row.currency)}</td>
                  <td>{statusBadge(row.status, row.status_label)}</td>
                  <td>{row.requested_by ? row.requested_by.name : "Apple (automatic)"}</td>
                  <td className="refundReason">{row.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </CustomTable>
        )}

        {/* ---------------- Apple requests ---------------- */}
        {activeTab === "apple" && (
          <>
            <div className="refundNotice">
              <FontAwesomeIcon icon={faCircleInfo} />
              <div>
                <strong>Apple decides these refunds, not us.</strong> When a customer asks
                Apple for a refund, we can tell Apple how much of the purchase they already
                used and say whether we think the refund should be given. Apple takes that
                into account but makes the final decision. Answer within 12 hours or Apple
                stops listening.
              </div>
            </div>

            <CustomTable
              headers={[
                { key: "customer", title: "Customer" },
                { key: "used", title: "How much they used", align: "center" },
                { key: "deadline", title: "Time left" },
                { key: "consent", title: "Can we reply?" },
                { key: "status", title: "Status" },
                { key: "action", title: "", align: "right" },
              ]}
            >
              <tbody>
                {!loading && appleRequests.length === 0 && (
                  <tr>
                    <td colSpan={6} className="refundEmpty">
                      No refund requests from Apple.
                    </td>
                  </tr>
                )}
                {appleRequests.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="refundCustomer">
                        <strong>{row.user?.name}</strong>
                        <small>{row.user?.email}</small>
                      </div>
                    </td>
                    <td className="text-center">
                      <strong>{(row.consumption_percentage / 1000).toFixed(1)}%</strong>
                      <br />
                      <small>
                        {row.credits_consumed} of {row.credits_granted} credits
                      </small>
                    </td>
                    <td>
                      {row.status !== "pending" ? (
                        "—"
                      ) : row.is_overdue ? (
                        <span className="refundStatus bad">
                          <FontAwesomeIcon icon={faClock} /> Expired
                        </span>
                      ) : (
                        <span
                          className={`refundStatus ${row.hours_remaining < 3 ? "bad" : "warn"}`}
                        >
                          <FontAwesomeIcon icon={faClock} /> {row.hours_remaining}h left
                        </span>
                      )}
                    </td>
                    <td>
                      {row.customer_consented ? (
                        <span className="refundStatus ok">
                          <FontAwesomeIcon icon={faCheckCircle} /> Yes
                        </span>
                      ) : (
                        <span className="refundStatus muted" title="The customer has not agreed to share this data with Apple, so we are not allowed to reply.">
                          <FontAwesomeIcon icon={faBan} /> No consent
                        </span>
                      )}
                    </td>
                    <td>
                      {statusBadge(
                        row.status === "pending" ? "pending" : row.status === "submitted" ? "succeeded" : "failed",
                        {
                          pending: "Waiting for you",
                          submitted: `Sent (${row.refund_preference === "DECLINE" ? "asked to decline" : "agreed"})`,
                          skipped_no_consent: "Cannot reply",
                          failed: "Failed",
                          expired: "Window closed",
                        }[row.status] || row.status
                      )}
                      {row.outcome && (
                        <>
                          <br />
                          <small>
                            Apple: {{ REFUND_DECLINED: "declined the refund", REFUND: "refunded",
                              REFUND_REVERSED: "reversed the refund", REVOKE: "revoked access" }[row.outcome] || row.outcome}
                          </small>
                        </>
                      )}
                    </td>
                    <td className="text-end">
                      {row.status === "pending" && row.customer_consented && !row.is_overdue && (
                        <>
                          <CustomButton
                            variant="primaryButton"
                            className="me-2"
                            text={row.credits_consumed > 0 ? "Ask Apple to decline" : "Agree to refund"}
                            onClick={() =>
                              submitToApple(row, row.credits_consumed > 0 ? "DECLINE" : "GRANT_FULL")
                            }
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </CustomTable>
          </>
        )}

        {/* ---------------- Refund modal ---------------- */}
        <Modal show={!!selected} centered onHide={() => setSelected(null)} className="refundModal">
          <button className="closeButton" onClick={() => setSelected(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <Modal.Body>
            {selected?.provider === "apple" && (
              <>
                <h2 className="modalHeading">Apple refund request</h2>
                <p>The customer must request a refund using the Apple Account that paid
                  for this purchase. Apple decides eligibility and the refund amount.</p>
                <p>Recording a request here does not send it to Apple, return money,
                  or remove access and credits.</p>
                <a href={selected.refund_url || "https://reportaproblem.apple.com/"}
                  target="_blank" rel="noopener noreferrer">Customer refund page on Apple</a>
                <div className="refundPartial">
                  <label>Reason (optional)</label>
                  <input type="text" value={reason} maxLength={500}
                    onChange={(event) => setReason(event.target.value)} />
                </div>
                <CustomButton variant="primaryButton" disabled={submitting}
                  text={submitting ? "Recording…" : "Record request"} onClick={submitRefund} />
              </>
            )}
            {selected && selected.provider !== "apple" && !confirming && (
              <>
                <h2 className="modalHeading">
                  <FontAwesomeIcon icon={faReceipt} /> Refund {selected.user?.name}
                </h2>

                <div className="refundFacts">
                  <div>
                    <span>They paid</span>
                    <strong>{money(selected.price_paid, selected.currency)}</strong>
                  </div>
                  <div>
                    <span>Credits bought</span>
                    <strong>{selected.credits_granted}</strong>
                  </div>
                  <div>
                    <span>Already used</span>
                    <strong>{selected.credits_consumed}</strong>
                  </div>
                  <div className="highlight">
                    <span>Can be refunded</span>
                    <strong>{selected.max_refundable_credits} credits</strong>
                  </div>
                </div>

                {selected.credits_consumed > 0 && (
                  <div className="refundNotice small">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <div>
                      {selected.credits_consumed} credit(s) have already been spent on reports
                      — worth {money(selected.consumed_exposure, selected.currency)}. Those
                      cannot be refunded, because the reports were already produced.
                    </div>
                  </div>
                )}

                {refundSource === "all" && <p className="refundNotice">This is an exceptional refund outside the reviewed worklist. Enter a reason to continue.</p>}
                <div className="refundChoice">
                  <label className={refundMode === "full" ? "active" : ""}>
                    <input
                      type="radio"
                      checked={refundMode === "full"}
                      onChange={() => setRefundMode("full")}
                    />
                    <div>
                      <strong>Refund everything unused</strong>
                      <small>
                        {selected.max_refundable_credits} credits ={" "}
                        {money(selected.max_refundable_amount, selected.currency)}
                      </small>
                    </div>
                  </label>

                  <label className={refundMode === "partial" ? "active" : ""}>
                    <input
                      type="radio"
                      checked={refundMode === "partial"}
                      onChange={() => setRefundMode("partial")}
                    />
                    <div>
                      <strong>Refund part of it</strong>
                      <small>Choose how many credits to give back</small>
                    </div>
                  </label>
                </div>

                {refundMode === "partial" && (
                  <div className="refundPartial">
                    <label>How many credits should we refund?</label>
                    <input
                      type="number"
                      min="1"
                      max={selected.max_refundable_credits}
                      value={partialCredits}
                      onChange={(e) => setPartialCredits(e.target.value)}
                      placeholder={`1 to ${selected.max_refundable_credits}`}
                    />
                    {partialError ? (
                      <p className="refundError">{partialError}</p>
                    ) : (
                      creditsToRefund > 0 && (
                        <p className="refundComputed">
                          That works out to{" "}
                          <strong>{money(amountToRefund, selected.currency)}</strong> back to
                          the customer.
                        </p>
                      )
                    )}
                  </div>
                )}

                <div className="refundPartial">
                  <label>Why is this being refunded? ({refundSource === "all" ? "required" : "optional"}, saved for your records)</label>
                  <input
                    type="text"
                    value={reason}
                    required={refundSource === "all"}
                    maxLength={500}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Customer asked, reports were not useful"
                  />
                </div>

                <div className="refundActions">
                  <CustomButton
                    variant="primaryButton"
                    className="me-2"
                    text="Continue"
                    disabled={!canSubmit}
                    onClick={() => setConfirming(true)}
                  />
                  <CustomButton
                    variant="secondaryButton"
                    text="Cancel"
                    onClick={() => setSelected(null)}
                  />
                </div>
              </>
            )}

            {selected && selected.provider !== "apple" && confirming && (
              <>
                <h2 className="modalHeading">Please confirm</h2>
                <div className="refundConfirm">
                  <p>
                    You are about to refund <strong>{money(amountToRefund, selected.currency)}</strong>{" "}
                    to <strong>{selected.user?.name}</strong>.
                  </p>
                  <ul>
                    <li>
                      <strong>{creditsToRefund}</strong> credit(s) will be taken off their
                      account.
                    </li>
                    <li>
                      They will keep the <strong>{selected.credits_consumed}</strong> credit(s)
                      they already used.
                    </li>
                    {creditsToRefund >= selected.max_refundable_credits && (
                      <li>Their subscription will end, because no credits will be left.</li>
                    )}
                    <li>
                      {selected.provider === "apple"
                        ? "Apple controls the money for this purchase."
                        : "The money goes back to their card automatically."}
                    </li>
                  </ul>
                  <p className="refundIrreversible">
                    <FontAwesomeIcon icon={faTriangleExclamation} /> This cannot be undone.
                  </p>
                </div>

                <div className="refundActions">
                  <CustomButton
                    variant="primaryButton"
                    className="me-2"
                    text={submitting ? "Refunding…" : "Yes, refund now"}
                    disabled={submitting}
                    onClick={submitRefund}
                  />
                  <CustomButton
                    variant="secondaryButton"
                    text="Go back"
                    disabled={submitting}
                    onClick={() => setConfirming(false)}
                  />
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
        <Modal show={Boolean(declining)} onHide={() => !submitting && setDeclining(null)} centered className="refundDeclineModal" aria-labelledby="refund-decline-title">
          <Modal.Header closeButton={!submitting}>
            <Modal.Title id="refund-decline-title">
              <span className="refundDeclineIcon"><FontAwesomeIcon icon={faBan} aria-hidden="true" /></span>
              Decline refund
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="refundDeclineSummary">
              <strong>{declining?.user?.name || "Customer"}</strong>
              <span>{declining?.plan}</span>
            </div>
            <p>Close this reviewed case without issuing a refund. The reason will remain visible in Cancellation Requests and All Purchases.</p>
            <div className="refundDeclineFieldLabel">
              <label htmlFor="refund-decline-reason">Reason <span>(required)</span></label>
              <small aria-hidden="true">{declineReason.length}/500</small>
            </div>
            <textarea id="refund-decline-reason" rows={4} maxLength={500} required disabled={submitting}
              placeholder="Explain why this refund is being declined"
              value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
          </Modal.Body>
          <Modal.Footer>
            <CustomButton type="button" variant="secondaryButton" text="Back" disabled={submitting} onClick={() => setDeclining(null)} />
            <CustomButton type="button" variant="primaryButton" text={submitting ? "Saving…" : "Decline and close"}
              icon={<FontAwesomeIcon icon={faBan} aria-hidden="true" />}
              disabled={submitting || !declineReason.trim()} onClick={declineRefund} />
          </Modal.Footer>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default RefundManagement;
